import React from 'react';

const useFileUpload = (
  handleDroppedFiles: (file: File) => void,
  handleDroppedMultipleFiles?: (files: File[]) => void
): {
  dragActive: boolean;
  fileRef: React.RefObject<HTMLInputElement>;
  dropZoneRef: React.RefCallback<HTMLDivElement>;
} => {
  const [dragActive, setIsDragOver] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  // Keep latest handlers in refs so the listeners (bound once, when the drop
  // zone mounts) always call through to the current callbacks.
  const handleDroppedFilesRef = React.useRef(handleDroppedFiles);
  const handleDroppedMultipleFilesRef = React.useRef(handleDroppedMultipleFiles);

  React.useEffect(() => {
    handleDroppedFilesRef.current = handleDroppedFiles;
    handleDroppedMultipleFilesRef.current = handleDroppedMultipleFiles;
  });

  // Detaches the listeners from the previously-attached node.
  const cleanupRef = React.useRef<(() => void) | undefined>(undefined);

  // Callback ref so listeners attach when the drop zone mounts, even late inside a MUI Dialog/Portal.
  const dropZoneRef = React.useCallback((dropZone: HTMLDivElement | null): void => {
    cleanupRef.current?.();
    cleanupRef.current = undefined;

    if (!dropZone) {
      return;
    }

    const handleDragEnter = (e: DragEvent): void => {
      e.preventDefault();
      setIsDragOver(true);
    };

    const handleDragOver = (e: DragEvent): void => {
      e.preventDefault();
      setIsDragOver(true);
    };

    const handleDragLeave = (e: DragEvent): void => {
      // Ignore dragleave fired when the cursor crosses onto a child element.
      if (e.relatedTarget && dropZone.contains(e.relatedTarget as Node)) return;
      setIsDragOver(false);
    };

    const handleDrop = (e: DragEvent): void => {
      e.preventDefault();
      setIsDragOver(false);

      const files = e.dataTransfer?.files;

      if (files && files.length > 0) {
        const onMultiple = handleDroppedMultipleFilesRef.current;
        if (files.length > 1 && onMultiple) {
          onMultiple(Array.from(files));
        } else {
          handleDroppedFilesRef.current(files[0]);
        }
      }
    };

    dropZone.addEventListener('dragenter', handleDragEnter);
    dropZone.addEventListener('dragover', handleDragOver);
    dropZone.addEventListener('dragleave', handleDragLeave);
    dropZone.addEventListener('drop', handleDrop);

    cleanupRef.current = () => {
      dropZone.removeEventListener('dragenter', handleDragEnter);
      dropZone.removeEventListener('dragleave', handleDragLeave);
      dropZone.removeEventListener('dragover', handleDragOver);
      dropZone.removeEventListener('drop', handleDrop);
    };
  }, []);

  return {
    dragActive,
    fileRef,
    dropZoneRef,
  };
};

export default useFileUpload;
